import type { ReactNode } from 'react';
import { Children, cloneElement, createContext, isValidElement, useContext } from 'react';

import { Field, FieldError, FieldHelp, FieldLabel } from '../../ui/field.js';
import { FormGridCol, type FormGridSpan } from '../form-grid/form-grid.js';

import { resolveFieldControlWidthClass } from './field-control-width.js';
import type { FieldKind, FormFieldSurface } from './field-kind.js';
import { resolveFieldSpan } from './resolve-field-span.js';

const LEADING_LOWERCASE_LETTER = /^\p{Ll}/u;

/** Uppercases only the first character when it is a lowercase letter (BR-IDT-58). */
function capitalizeFirstLetter(value: string): string {
  const leading = LEADING_LOWERCASE_LETTER.exec(value);
  if (leading === null) {
    return value;
  }

  const first = leading[0];
  return `${first.toLocaleUpperCase('pt-BR')}${value.slice(first.length)}`;
}
const FIRST_LETTER_UPPERCASE_FIELD_KINDS: ReadonlySet<FieldKind> = new Set([
  'name',
  'legalName',
  'tradeName',
  'street',
  'neighborhood',
  'complement',
  'reference',
  'textarea',
  'nickname',
  'occupation',
  'jobTitle',
  'color',
  'owner',
]);

const RegisterFirstLetterCaseContext = createContext(false);

/** Enables first-letter capitalization for register FormFields in the tree. */
export function RegisterFirstLetterCaseProvider({ children }: { children: ReactNode }) {
  return (
    <RegisterFirstLetterCaseContext.Provider value={true}>
      {children}
    </RegisterFirstLetterCaseContext.Provider>
  );
}

interface FormFieldControlChangeEvent {
  target: { value: unknown };
}

interface FormFieldControlProps {
  required?: boolean;
  'aria-required'?: boolean;
  onChange?: (event: FormFieldControlChangeEvent) => void;
}

export interface FormFieldProps {
  kind: FieldKind;
  surface?: FormFieldSurface;
  /** Overrides {@link resolveFieldSpan} when the default grid placement does not fit the row. */
  span?: FormGridSpan;
  label: ReactNode;
  required?: boolean;
  htmlFor?: string;
  error?: ReactNode;
  help?: ReactNode;
  children: ReactNode;
  className?: string;
  /** When true, capitalizes the first letter of free-text kinds (BR-IDT-58). Default off. */
  firstLetterCase?: boolean;
}

function applyControlProps(
  children: ReactNode,
  required: boolean,
  capitalizeFirst: boolean,
): ReactNode {
  if (!required && !capitalizeFirst) {
    return children;
  }

  return Children.map(children, (child) => {
    if (!isValidElement<FormFieldControlProps>(child)) {
      return child;
    }

    const next: FormFieldControlProps = {};
    if (required) {
      next.required = true;
      next['aria-required'] = true;
    }
    if (capitalizeFirst) {
      const originalOnChange: FormFieldControlProps['onChange'] = child.props.onChange;
      next.onChange = (event: FormFieldControlChangeEvent) => {
        if (typeof event.target.value === 'string') {
          event.target.value = capitalizeFirstLetter(event.target.value);
        }
        originalOnChange?.(event);
      };
    }

    return cloneElement(child, next);
  });
}

/** Form grid cell with label + control — span from {@link FieldKind} + surface. */
export function FormField({
  kind,
  surface = 'page',
  span,
  label,
  required = false,
  htmlFor,
  error,
  help,
  children,
  className,
  firstLetterCase,
}: FormFieldProps) {
  const registerFirstLetterCase = useContext(RegisterFirstLetterCaseContext);
  const capitalizeFirst =
    FIRST_LETTER_UPPERCASE_FIELD_KINDS.has(kind) &&
    (firstLetterCase === true || registerFirstLetterCase);
  const resolvedSpan = span ?? resolveFieldSpan(kind, surface);
  const controlWidthClass = resolveFieldControlWidthClass(kind);

  return (
    <FormGridCol span={resolvedSpan} className={className}>
      <Field data-required={required ? 'true' : undefined}>
        <FieldLabel htmlFor={htmlFor} required={required}>
          {label}
        </FieldLabel>
        <div className={`field-control ${controlWidthClass}`}>
          {applyControlProps(children, required, capitalizeFirst)}
        </div>
        {error ? <FieldError>{error}</FieldError> : null}
        {help ? <FieldHelp>{help}</FieldHelp> : null}
      </Field>
    </FormGridCol>
  );
}
